import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Save } from 'lucide-react';
import { 
  SniperRule, 
  SniperCondition, 
  SNIPER_FIELD_LABELS, 
  SNIPER_OPERATOR_LABELS, 
  SNIPER_TIMEFRAME_LABELS 
} from '../../services/sniperRulesService';

interface SniperRuleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (name: string, conditions: Omit<SniperCondition, 'id' | 'rule_id' | 'created_at'>[]) => Promise<void>;
  editingRule?: SniperRule | null;
}

const SniperRuleModal: React.FC<SniperRuleModalProps> = ({
  isOpen,
  onClose,
  onSave,
  editingRule
}) => {
  const [ruleName, setRuleName] = useState('');
  const [conditions, setConditions] = useState<Omit<SniperCondition, 'id' | 'rule_id' | 'created_at'>[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Initialize form with editing rule data
  useEffect(() => {
    if (editingRule) {
      setRuleName(editingRule.name);
      setConditions(editingRule.conditions.map(c => ({
        field: c.field,
        operator: c.operator,
        value: c.value,
        timeframe: c.timeframe
      })));
    } else {
      setRuleName('');
      setConditions([]);
    }
    setErrors({});
  }, [editingRule, isOpen]);

  // Add a new condition
  const addCondition = () => {
    setConditions([...conditions, {
      field: 'marketCap',
      operator: '>',
      value: 0
    }]);
  };

  // Remove a condition
  const removeCondition = (index: number) => {
    setConditions(conditions.filter((_, i) => i !== index));
  };

  // Update a condition
  const updateCondition = (index: number, updates: Partial<SniperCondition>) => {
    const newConditions = [...conditions];
    newConditions[index] = { ...newConditions[index], ...updates };
    setConditions(newConditions);
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!ruleName.trim()) {
      newErrors.ruleName = 'Rule name is required';
    }

    if (conditions.length === 0) {
      newErrors.conditions = 'At least one condition is required';
    }

    conditions.forEach((condition, index) => {
      // For name and symbol fields, validate as string
      if (condition.field === 'name' || condition.field === 'symbol') {
        if (!condition.value || String(condition.value).trim().length === 0) {
          newErrors[`condition_${index}_value`] = 'Value is required';
        }
      } else {
        // For numeric fields, validate as number
        if (condition.value <= 0) {
          newErrors[`condition_${index}_value`] = 'Value must be greater than 0';
        }
      }

      if ((condition.operator === 'increase%' || condition.operator === 'decrease%') && !condition.timeframe) {
        newErrors[`condition_${index}_timeframe`] = 'Timeframe is required for percentage operators';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle save
  const handleSave = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      await onSave(ruleName, conditions);
      onClose();
    } catch (error) {
      console.error('Error saving rule:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle condition field change
  const handleFieldChange = (index: number, field: string) => {
    const condition = conditions[index];
    const updates: any = { field };

    // Reset operator if not compatible with field
    if ((field === 'marketCap' || field === 'liquidity' || field === 'txVolume') && 
        (condition.operator === 'increase%' || condition.operator === 'decrease%')) {
      updates.operator = '>';
    }
    
    // Set operator to '=' for name and symbol fields (only allowed operator)
    if (field === 'name' || field === 'symbol') {
      updates.operator = '=';
    }

    updateCondition(index, updates);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[#16161a] border border-gray-700/20 rounded-lg w-full max-w-md shadow-xl">
        {/* Header */}
        <div className="p-4 border-b border-gray-700/20 flex items-center justify-between">
          <h3 className="text-white text-lg font-semibold">
            {editingRule ? 'Edit Sniper Rule' : 'Create Sniper Rule'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Rule Name */}
          <div>
            <label className="text-white text-sm font-medium mb-2 block">
              Rule Name
            </label>
            <input
              type="text"
              value={ruleName}
              onChange={(e) => setRuleName(e.target.value)}
              className={`w-full bg-white border rounded px-3 py-2 text-black text-sm focus:outline-none focus:border-[#24c35b]/50 ${
                errors.ruleName ? 'border-red-500' : 'border-gray-200'
              }`}
              placeholder="Enter rule name"
            />
            {errors.ruleName && (
              <p className="text-red-400 text-xs mt-1">{errors.ruleName}</p>
            )}
          </div>

          {/* Conditions */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-white text-sm font-medium">
                Conditions
              </label>
              <button
                onClick={addCondition}
                className="px-2 py-1 rounded bg-[#24c35b]/20 border border-[#24c35b]/40 text-[#24c35b] text-xs hover:bg-[#24c35b]/30 transition-all"
              >
                <Plus size={12} className="mr-1 inline" />
                Add Condition
              </button>
            </div>

            {errors.conditions && (
              <p className="text-red-400 text-xs mb-2">{errors.conditions}</p>
            )}

            <div className="space-y-3">
              {conditions.map((condition, index) => (
                <div
                  key={index}
                  className="p-3 rounded-lg border border-gray-600/20 bg-gray-600/5"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white text-xs font-medium">
                      Condition {index + 1}
                    </span>
                    <button
                      onClick={() => removeCondition(index)}
                      className="text-gray-400 hover:text-red-400 transition-colors"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>

                  <div className="grid grid-cols-2 gap-2 mb-2">
                    {/* Field */}
                    <div>
                      <label className="text-gray-400 text-xs mb-1 block">Field</label>
                      <select
                        value={condition.field}
                        onChange={(e) => handleFieldChange(index, e.target.value)}
                        className="w-full bg-white border border-gray-200 rounded px-2 py-1 text-black text-xs focus:outline-none focus:border-[#24c35b]/50"
                      >
                        {Object.entries(SNIPER_FIELD_LABELS).map(([value, label]) => (
                          <option key={value} value={value}>{label}</option>
                        ))}
                      </select>
                    </div>

                    {/* Operator */}
                    <div>
                      <label className="text-gray-400 text-xs mb-1 block">Operator</label>
                      <select
                        value={condition.operator}
                        onChange={(e) => updateCondition(index, { operator: e.target.value as any })}
                        className="w-full bg-white border border-gray-200 rounded px-2 py-1 text-black text-xs focus:outline-none focus:border-[#24c35b]/50"
                      >
                        {Object.entries(SNIPER_OPERATOR_LABELS).map(([value, label]) => {
                          // Hide percentage operators for certain fields
                          if ((value === 'increase%' || value === 'decrease%') && 
                              !['marketCap', 'liquidity', 'holders', 'txVolume'].includes(condition.field)) {
                            return null;
                          }
                          
                          // Only show '=' operator for name and symbol fields
                          if ((condition.field === 'name' || condition.field === 'symbol') && value !== '=') {
                            return null;
                          }
                          
                          return <option key={value} value={value}>{label}</option>;
                        })}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    {/* Value */}
                    <div>
                      <label className="text-gray-400 text-xs mb-1 block">Value</label>
                      {(condition.field === 'name' || condition.field === 'symbol') ? (
                        <input
                          type="text"
                          value={String(condition.value || '')}
                          onChange={(e) => {
                            // Store string value directly (will be handled as string in comparison logic)
                            updateCondition(index, { value: e.target.value as any });
                          }}
                          className={`w-full bg-white border rounded px-2 py-1 text-black text-xs focus:outline-none focus:border-[#24c35b]/50 ${
                            errors[`condition_${index}_value`] ? 'border-red-500' : 'border-gray-200'
                          }`}
                          placeholder="Enter token name or symbol"
                        />
                      ) : (
                        <input
                          type="text"
                          value={condition.value}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === '' || /^\d*\.?\d*$/.test(value)) {
                              updateCondition(index, { value: parseFloat(value) || 0 });
                            }
                          }}
                          className={`w-full bg-white border rounded px-2 py-1 text-black text-xs focus:outline-none focus:border-[#24c35b]/50 ${
                            errors[`condition_${index}_value`] ? 'border-red-500' : 'border-gray-200'
                          }`}
                          placeholder="0"
                          inputMode="decimal"
                        />
                      )}
                      {errors[`condition_${index}_value`] && (
                        <p className="text-red-400 text-xs mt-1">{errors[`condition_${index}_value`]}</p>
                      )}
                    </div>

                    {/* Timeframe (only for percentage operators) */}
                    {(condition.operator === 'increase%' || condition.operator === 'decrease%') && (
                      <div>
                        <label className="text-gray-400 text-xs mb-1 block">Timeframe</label>
                        <select
                          value={condition.timeframe || ''}
                          onChange={(e) => updateCondition(index, { timeframe: e.target.value as any })}
                          className={`w-full bg-white border rounded px-2 py-1 text-black text-xs focus:outline-none focus:border-[#24c35b]/50 ${
                            errors[`condition_${index}_timeframe`] ? 'border-red-500' : 'border-gray-200'
                          }`}
                        >
                          <option value="">Select timeframe</option>
                          {Object.entries(SNIPER_TIMEFRAME_LABELS).map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                          ))}
                        </select>
                        {errors[`condition_${index}_timeframe`] && (
                          <p className="text-red-400 text-xs mt-1">{errors[`condition_${index}_timeframe`]}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {conditions.length === 0 && (
                <div className="text-center py-4">
                  <p className="text-gray-400 text-xs">No conditions added yet</p>
                  <p className="text-gray-500 text-xs mt-1">Click "Add Condition" to get started</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-700/20 flex items-center justify-end space-x-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-gray-600/50 text-gray-400 hover:text-white hover:border-gray-500 transition-all text-sm"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isLoading}
            className="px-4 py-2 rounded-lg bg-[#24c35b]/20 border border-[#24c35b]/40 text-[#24c35b] hover:bg-[#24c35b]/30 transition-all text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <>Saving...</>
            ) : (
              <>
                <Save size={14} className="mr-1 inline" />
                {editingRule ? 'Update Rule' : 'Create Rule'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SniperRuleModal; 